import type { ApolloError } from 'apollo-client';
import type { MessageDescriptor } from 'react-intl-next';

import { containsNoNetworkError } from '@confluence/network';

import { ObjectCommentsError } from './ObjectCommentsError';
import { i18n } from './objectCommentsi18n';

type ParsedError = {
	message: string;
	statusCode?: number;
};

const parseError: (error: Error | ApolloError) => ParsedError = (error) => {
	const { message } = error;
	let errorMessage: string;

	/* If this is a GraphQL error we need to extract the data out of it
	 * GraphQL errors that are returned from queries/mutations always have the same format:
	 * GraphQL error: <GUID>:[ <EXCEPTION>:] <REASON>
	 */
	if (message.indexOf('GraphQL error:') !== -1) {
		const reasonStartIdx = message.lastIndexOf(':') + 1;
		errorMessage = message.substr(reasonStartIdx, message.length - reasonStartIdx).trim();
	} else {
		errorMessage = message;
	}

	const parsedError: ParsedError = {
		message: errorMessage,
	};

	// If we have an ApolloError, extract the status code and the authorization status
	const { graphQLErrors } = error as ApolloError;
	if (graphQLErrors && graphQLErrors.length) {
		parsedError.statusCode = graphQLErrors?.[0]?.extensions?.statusCode;
	}

	return parsedError;
};

export const getTranslatedError: (error: Error | ApolloError) => MessageDescriptor = (error) => {
	if (containsNoNetworkError(error)) {
		return i18n.networkError;
	}

	const parsedError = parseError(error);
	// use switch-case statement when we have more than 2 error types
	if (parsedError.message === ObjectCommentsError.VIEW_NOT_PERMITTED) {
		return i18n.viewNotPermitted;
	} else {
		return i18n.unexpectedError;
	}
};

export const isViewNotPermittedError = (translatedMessage: MessageDescriptor) =>
	translatedMessage.id === i18n.viewNotPermitted.id;

export const isNetworkError = (translatedMessage: MessageDescriptor) =>
	translatedMessage.id === i18n.networkError.id;
